/**
 * Licenze on-prem (ADR-0073) — the on-prem ORGANIZATION catalog. An on-prem install is NOT
 * a SaaS tenant: its identity IS the license key row (organization, plan entitlements,
 * expiry, heartbeat). Mint an activation key (shown ONCE — only its hash is stored),
 * list the fleet's licenses with liveness, revoke (the kill-switch: the install goes
 * RESTRICTED at its next heartbeat).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Copy,
  KeySquare,
  Plus,
  ShieldOff, Unlink,
  SlidersHorizontal,
  TriangleAlert,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { EntitlementsEditor } from "@/components/platform/EntitlementsEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  type BillingPeriod,
  deriveEndDate,
  diffOverrides,
  fullEntitlements,
  overridesCount,
} from "@/lib/licenseEntitlements";
import { reportSummary, reportVersion } from "@/lib/licenseReport";
import {
  type PlanEntitlements,
  type PlatformLicense,
  type PlatformLicenseMint,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";


const LICENSES_KEY = ["platform", "licenses"];

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function StatusBadge({ lic }: { lic: PlatformLicense }) {
  if (lic.status === "revoked") return <Badge tone="danger">Revocata</Badge>;
  // No issuance = no validity to judge. It cannot be signed at all (ADR-0076 decision 8),
  // so calling it "Scaduta" would send the operator chasing a renewal that isn't the fix.
  if (!lic.expires_at) return <Badge tone="warning">Nessuna emissione</Badge>;
  if (new Date(lic.expires_at).getTime() < Date.now())
    return <Badge tone="warning">Scaduta</Badge>;
  if (!lic.activated_at) return <Badge tone="draft">Da attivare</Badge>;
  return <Badge tone="active">Attiva</Badge>;
}

/**
 * What the install told us about itself on its last heartbeat (ADR-0074 L1) — the answer
 * to "la licenza è l'unico dato che ho di un on-premise". An install that has never
 * reported shows an explicit "Nessun dato", NOT zeros: a hub older than L1 is silent, not
 * empty, and inventing zeros here would read as a real (alarming) number.
 */
function InstallCell({ lic }: { lic: PlatformLicense }) {
  const version = reportVersion(lic.last_report);
  const summary = reportSummary(lic.last_report);
  if (!version && !summary)
    return <span className="text-xs text-muted-foreground">Nessun dato</span>;
  return (
    <div className="leading-tight">
      {version && <div className="font-mono text-xs">v{version}</div>}
      {summary && <div className="text-xs text-muted-foreground">{summary}</div>}
    </div>
  );
}

function MintDialog({ onClose, onMinted }: { onClose: () => void; onMinted: () => void }) {
  const [organization, setOrganization] = React.useState("");
  const [planKey, setPlanKey] = React.useState("");
  // The window is stated ONCE: start + cadence, end derived (the issuer derives it again
  // — this is the preview). Only a `custom` period asks for an explicit end date.
  const [period, setPeriod] = React.useState<BillingPeriod>("annual");
  const [startsAt, setStartsAt] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [customEnd, setCustomEnd] = React.useState("");
  const [graceDays, setGraceDays] = React.useState(15);
  const [customising, setCustomising] = React.useState(false);
  const [ent, setEnt] = React.useState<PlanEntitlements | null>(null);
  const [minted, setMinted] = React.useState<PlatformLicenseMint | null>(null);
  const [copied, setCopied] = React.useState(false);

  // The entitlements are COPIED from a plan at mint (the license snapshot model —
  // editing the plan later never changes an issued license).
  const plansQ = useQuery({
    queryKey: ["platform", "plans"],
    queryFn: () => platformApi.listPlans(),
  });
  const plans = plansQ.data ?? [];
  const selectedPlan = plans.find((p) => p.key === planKey) ?? null;
  const planEnt = (selectedPlan?.entitlements ?? null) as PlanEntitlements | null;

  // The axes live in backend code, so the customisation form is generated — a new axis
  // reaches this dialog with a deploy, not a FE change.
  const catalogQ = useQuery({
    queryKey: ["platform", "entitlements-catalog"],
    queryFn: () => platformApi.entitlementsCatalog(),
  });
  const catalog = catalogQ.data ?? null;

  // Switching plan RESEEDS the customisation: overrides are a delta over THIS plan, so
  // carrying the previous plan's edits forward would silently mean something else.
  React.useEffect(() => {
    setEnt(catalog ? fullEntitlements(catalog, planEnt) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, catalog]);

  const baseline = catalog ? fullEntitlements(catalog, planEnt) : null;
  const overrides = catalog && ent ? diffOverrides(catalog, planEnt, ent) : null;
  const derivedEnd = deriveEndDate(startsAt, period);
  const endDate = period === "custom" ? customEnd : derivedEnd;

  // An on-prem install belongs to a company that usually ALREADY exists (its second
  // deployment, or one that also has a cloud workspace). Typing the name again minted a
  // second anagrafica for the same company — the duplicate the model exists to avoid.
  // So the field PICKS from the catalog, and free text is the deliberate fallback for a
  // genuinely new one (the backend then creates-or-reuses by name).
  const organizationsQ = useQuery({
    queryKey: ["platform", "organizations", { status: "active" }],
    queryFn: () => platformApi.listOrganizations({ status: "active" }),
  });
  const organizations = organizationsQ.data ?? [];
  const picked = organizations.find((o) => o.id === organization) ?? null;
  const organizationName = picked ? picked.name : organization.trim();

  const mint = useMutation({
    mutationFn: () =>
      platformApi.mintLicense({
        organization: organizationName,
        // An explicit id ATTACHES to that company; omitting it lets the mint create-or-reuse
        // one named after the label — the two branches the route already has.
        organization_id: picked ? picked.id : undefined,
        entitlements: selectedPlan ? (selectedPlan.entitlements as never) : {},
        // Only the DELTA — sending the whole edited blob would pin every untouched axis
        // to today's plan values, so a later plan fix would stop reaching this licence.
        overrides: (overrides as Record<string, unknown> | null) ?? undefined,
        billing_period: period,
        starts_at: new Date(`${startsAt}T00:00:00Z`).toISOString(),
        // Derived server-side for monthly/annual; only `custom` states it.
        expires_at:
          period === "custom" && customEnd
            ? new Date(`${customEnd}T23:59:59Z`).toISOString()
            : undefined,
        grace_days: graceDays,
      }),
    onSuccess: (res) => {
      setMinted(res);
      onMinted();
    },
  });

  if (minted) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Chiave di attivazione — {minted.organization}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-sm text-warning">
              Questa chiave è mostrata UNA sola volta: viene conservato solo il suo hash.
              Consegnala all'organizzazione ora.
            </p>
            <div className="flex items-center gap-2 rounded-md border border-border/60 bg-surface p-3">
              <code className="flex-1 break-all font-mono text-xs">{minted.activation_key}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(minted.activation_key);
                  setCopied(true);
                }}
              >
                <Copy className="h-4 w-4" strokeWidth={1.5} />
                {copied ? "Copiata" : "Copia"}
              </Button>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuova licenza on-prem</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="lic-organization">Organizzazione</Label>
            <Combobox
              id="lic-organization"
              options={organizations.map((o) => ({
                value: o.id,
                label: o.name,
                description: o.legal_name && o.legal_name !== o.name ? o.legal_name : undefined,
              }))}
              value={organization || null}
              onChange={(v) => setOrganization(v ?? "")}
              allowCustom
              loading={organizationsQ.isLoading}
              placeholder="Scegli o scrivi il nome…"
              searchPlaceholder="Nome o ragione sociale…"
              emptyText="Nessuna corrispondenza — invio per crearne una nuova"
              renderValue={(opt, raw) => opt?.label ?? raw}
            />
            <p className="text-xs text-muted-foreground">
              {picked
                ? "La licenza viene agganciata a questa organizzazione."
                : organizationName
                  ? `Nessuna corrispondenza: viene creata (o riusata) l'organizzazione «${organizationName}».`
                  : "Scegli un'organizzazione esistente, o scrivi un nome nuovo."}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lic-plan">Piano (entitlements copiati alla generazione)</Label>
            <select id="lic-plan" value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
              <option value="">— scegli un piano —</option>
              {plans.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name} ({p.key})
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              La licenza fotografa gli entitlements del piano ADESSO: modificare il piano dopo
              non cambia le licenze già emesse.
            </p>
          </div>
          {/* The window is stated ONCE — start + cadence — and the end date follows.
              Asking for the period AND the end date is how the two drift: the renewal
              derives its own window from `billing_period`, so a hand-typed end date that
              disagrees with it silently moves the customer's renewal. */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="lic-period">Periodo</Label>
              <select
                id="lic-period"
                value={period}
                onChange={(e) => setPeriod(e.target.value as BillingPeriod)}
              >
                <option value="annual">Annuale</option>
                <option value="monthly">Mensile</option>
                <option value="custom">Personalizzato</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lic-starts">Data di inizio</Label>
              <Input
                id="lic-starts"
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lic-expires">Scadenza</Label>
              {period === "custom" ? (
                <Input
                  id="lic-expires"
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              ) : (
                <div
                  id="lic-expires"
                  className="flex h-8 items-center rounded-md border border-border/60 bg-elevated px-2 text-sm text-muted-foreground"
                >
                  {derivedEnd ? new Date(derivedEnd).toLocaleDateString() : "—"}
                </div>
              )}
              {period !== "custom" && (
                <p className="text-[10px] text-muted-foreground">calcolata dal periodo</p>
              )}
            </div>
          </div>
          <div className="space-y-1.5 sm:max-w-[12rem]">
            <Label htmlFor="lic-grace">Giorni di grazia</Label>
            <Input
              id="lic-grace"
              type="number"
              min={0}
              max={365}
              value={graceDays}
              onChange={(e) => setGraceDays(Number(e.target.value) || 0)}
            />
          </div>

          {/* The plan is a TEMPLATE: this is where a single licence deviates from it.
              Collapsed by default so the common "just use the plan" mint stays two
              fields, and only the axes actually changed are sent as overrides. */}
          {selectedPlan && catalog && ent && (
            <div className="rounded-md border border-border/60">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm"
                onClick={() => setCustomising((v) => !v)}
              >
                <span className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} />
                  Personalizza per questa licenza
                </span>
                {overridesCount(overrides) > 0 ? (
                  <Badge tone="warning">{overridesCount(overrides)} modifiche</Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">come da piano</span>
                )}
              </button>
              {customising && (
                <div className="space-y-3 border-t border-border/60 p-3">
                  <p className="text-xs text-muted-foreground">
                    Il piano è un modello: qui cambi cap e funzionalità solo per questa licenza.
                    Ciò che non tocchi resta ereditato dal piano.
                  </p>
                  <EntitlementsEditor
                    catalog={catalog}
                    value={ent}
                    onChange={setEnt}
                    baseline={baseline}
                  />
                </div>
              )}
            </div>
          )}
          {mint.isError && (
            <p className="text-sm text-danger">
              {mint.error instanceof PlatformApiError
                ? mint.error.message
                : "Generazione non riuscita."}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            onClick={() => mint.mutate()}
            disabled={!organizationName || !selectedPlan || !startsAt || !endDate || mint.isPending}
          >
            Genera chiave
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PlatformLicensesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [minting, setMinting] = React.useState(false);

  const listQ = useQuery({ queryKey: LICENSES_KEY, queryFn: () => platformApi.listLicenses() });
  const invalidate = () => qc.invalidateQueries({ queryKey: LICENSES_KEY });

  const revoke = useMutation({
    mutationFn: (id: string) => platformApi.revokeLicense(id),
    onSuccess: invalidate,
  });

  const release = useMutation({
    mutationFn: (id: string) => platformApi.releaseLicenseInstallation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform-licenses"] }),
  });

  const rows = listQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Licenze on-prem"
        description="I organizzazioni on-prem della flotta: chiave di attivazione, entitlements, scadenza e heartbeat. La revoca manda l'installazione in modalità RISTRETTA al prossimo heartbeat."
        actions={
          <Button size="sm" onClick={() => setMinting(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Nuova licenza
          </Button>
        }
      />
      <PageContent className="space-y-4">
        {listQ.isError ? (
          <EmptyState
            icon={TriangleAlert}
            title="Impossibile caricare le licenze"
            action={
              <Button variant="ghost" onClick={() => listQ.refetch()}>
                Riprova
              </Button>
            }
          />
        ) : rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={KeySquare}
            title="Nessuna licenza emessa"
            description="Genera una chiave di attivazione per il primo organizzazione on-prem."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Organizzazione</Th>
                  <Th>Chiave</Th>
                  <Th>Stato</Th>
                  <Th>Scadenza</Th>
                  <Th>Installazione</Th>
                  <Th>Ultimo heartbeat</Th>
                  <Th className="w-28 text-right">Azioni</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((lic) => (
                  // The row is the way IN: everything that isn't the kill switch (the
                  // ledger, the Organizzazione link, what the licence includes) lives on
                  // the detail page rather than in a dialog stack over the table.
                  <Tr
                    key={lic.id}
                    className="cursor-pointer"
                    onClick={() => navigate(lic.id)}
                  >
                    <Td className="font-medium">{lic.organization}</Td>
                    <Td className="font-mono text-xs text-muted-foreground">
                      {lic.activation_key_prefix}…
                    </Td>
                    <Td>
                      <StatusBadge lic={lic} />
                    </Td>
                    <Td>
                      <div className="whitespace-nowrap">{fmtDate(lic.expires_at)}</div>
                      {lic.issuance_count > 1 && (
                        <div className="text-xs text-muted-foreground">
                          {lic.issuance_count} emissioni
                        </div>
                      )}
                    </Td>
                    <Td>
                      <InstallCell lic={lic} />
                    </Td>
                    <Td>{lic.heartbeat_at ? new Date(lic.heartbeat_at).toLocaleString() : "—"}</Td>
                    <Td className="text-right">
                      {/* The kill switch STAYS on the row: it is the one action taken in a
                          hurry, and making it a two-click journey is exactly wrong. */}
                      {/* The escape hatch for a DEAD box: the soft first-writer bind
                          refuses a different installation forever, so without this a
                          `down -v` burned the KEY (the live Noesis incident — the only
                          way out was minting a new licence). Releasing is NOT revoking:
                          the licence stays active, first-writer-wins re-arms. */}
                      {lic.status !== "revoked" && lic.installation_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={release.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              window.confirm(
                                `Sganciare l'installazione da "${lic.organization}"? La stessa chiave potrà attivare una nuova macchina (la vecchia, se viva, potrà ri-legarsi per prima).`,
                              )
                            )
                              release.mutate(lic.id);
                          }}
                        >
                          <Unlink className="h-4 w-4" strokeWidth={1.5} />
                          Sgancia
                        </Button>
                      )}
                      {lic.status !== "revoked" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-danger"
                          disabled={revoke.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              window.confirm(
                                `Revocare la licenza di "${lic.organization}"? L'installazione andrà in modalità ristretta.`,
                              )
                            )
                              revoke.mutate(lic.id);
                          }}
                        >
                          <ShieldOff className="h-4 w-4" strokeWidth={1.5} />
                          Revoca
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>
      {minting && <MintDialog onClose={() => setMinting(false)} onMinted={invalidate} />}
    </PageContainer>
  );
}
